import React from "react";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client";
import { Checkbox } from "@/components/ui/checkbox";
import { Service } from "@/pages/NewTaskPage/types";
import { Button } from "@/components/ui/button";
import { ServicesQuery, ServicesQueryVariables } from "@/__generated__/graphql";
import { cn } from "@/lib/utils";
import { Theme, useTheme } from "@/contexts/ThemeContext";

import RotoscopingIcon from "@/assets/rotoscoping.svg";
import RotoLight from "@/assets/rotoscoping_light.svg";
import PaintIcon from "@/assets/paint.svg";
import PaintLight from "@/assets/paint_light.svg";
import TrackingIcon from "@/assets/3d_tracking.svg";
import TrackingLight from "@/assets/3d_tracking_light.svg";
import CompositionIcon from "@/assets/composition.svg";
import CompositionLight from "@/assets/composition_light.svg";
import AssetsIcon from "@/assets/assets.svg";
import AssetsLight from "@/assets/assets_light.svg";
import TwoDTrackingIcon from "@/assets/2d_tracking.svg";
import TwoDTrackingLight from "@/assets/2d_tracking_light.svg";

export const serviceIcons: Record<string, string> = {
  Rotoscoping: RotoscopingIcon,
  Paint: PaintIcon,
  "3D Tracking": TrackingIcon,
  Compositing: CompositionIcon,
  Assets: AssetsIcon,
  "2D Tracking": TwoDTrackingIcon,
};

export const lightServiceIcons: Record<string, string> = {
  Rotoscoping: RotoLight,
  Paint: PaintLight,
  "3D Tracking": TrackingLight,
  Compositing: CompositionLight,
  Assets: AssetsLight,
  "2D Tracking": TwoDTrackingLight,
};

export const SERVICES_QUERY = gql`
  query Services {
    services {
      __typename
      name
      id
      shouldUseShotDetectionFlow
      subServices {
        name
        id
      }
    }
  }
`;

interface SelectServicesProps {
  selectedServices: Service[];
  onServiceSelect: (service: Service) => void;
  onNext?: () => void;
  excludedServiceId?: string;
}

const SelectServices: React.FC<SelectServicesProps> = ({
  selectedServices,
  onServiceSelect,
  onNext,
  excludedServiceId,
}) => {
  const { theme } = useTheme();
  const { data, loading, error } = useQuery<
    ServicesQuery,
    ServicesQueryVariables
  >(SERVICES_QUERY);

  const services = (data?.services ?? []).filter(
    (s: Service) => s.id !== excludedServiceId,
  );
  return (
    <div className="flex flex-col gap-[20px]">
      <h5 className="text-base text-secondary-foreground mx-[24px]">
        Select Services
      </h5>
      {loading && <div>Loading Services...</div>}
      {!loading && error && (
        <div>Error loading services: {error.toString()}</div>
      )}
      {!loading && !error && (!data || data?.services.length == 0) && (
        <div>No services found.</div>
      )}

      {!loading && !error && services.length > 0 && (
        <div className="flex flex-row gap-[16px] mx-[24px]">
          {services.map((service: Service) => (
            <div
              key={service.id}
              className={cn(
                "w-full aspect-[208/130] p-3 relative rounded-[4px] bg-primary border border-accent-foreground flex gap-2 cursor-pointer overflow-hidden",
                selectedServices?.length > 0 &&
                  service.shouldUseShotDetectionFlow !==
                    selectedServices[0].shouldUseShotDetectionFlow &&
                  "cursor-not-allowed",
              )}
              onClick={() => onServiceSelect(service)}
            >
              <div className="flex items-center gap-2 h-min">
                <Checkbox
                  id={`is${service.name}Selected`}
                  checked={selectedServices.some((s) => s.id === service.id)}
                  disabled={
                    selectedServices?.length > 0 &&
                    service.shouldUseShotDetectionFlow !==
                      selectedServices[0].shouldUseShotDetectionFlow
                  }
                />
                <h5
                  className={cn(
                    "text-sm text-primary-foreground",
                    selectedServices?.length > 0 &&
                      service.shouldUseShotDetectionFlow !==
                        selectedServices[0].shouldUseShotDetectionFlow &&
                      "text-muted-foreground",
                  )}
                >
                  {service.name}
                </h5>
              </div>
              <img
                src={
                  theme === Theme.Dark
                    ? serviceIcons[service.name]
                    : lightServiceIcons[service.name]
                }
                alt={`${service.name} icon`}
                className={cn(
                  "absolute right-0 bottom-1 !h-[calc(100%-20px)] object-contain",
                  theme !== Theme.Dark && "scale-[2.5]",
                )}
              />
            </div>
          ))}
        </div>
      )}
      <div className="mt-[40px] bg-primary py-[16px] px-[24px] flex justify-end rounded-b-[24px]">
        <Button
          type="submit"
          className="text-white py-[4px] px-[30px] rounded !border-white transition !bg-service-paint hover:opacity-80 disabled:opacity-50 "
          onClick={onNext}
          disabled={selectedServices.length === 0}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default SelectServices;
